import React, { createContext, useContext, useState, useEffect } from 'react';

const AppearanceContext = createContext();

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return context;
};

export const AppearanceProvider = ({ children }) => {
  const [appearanceMode, setAppearanceMode] = useState(() => {
    // Try to get saved preference from localStorage
    const saved = localStorage.getItem('supple-appearance-mode');
    // Default to 'supple-light' for everyone (including first-time visitors)
    return saved === null || saved === undefined ? 'supple-light' : saved;
  });

  // Save preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('supple-appearance-mode', appearanceMode);
    
    // Apply CSS classes to body based on appearance mode
    const body = document.body;
    body.classList.remove('appearance-default', 'appearance-supple', 'appearance-supple-light', 'appearance-supple-dark', 'appearance-plain');
    body.classList.add(`appearance-${appearanceMode}`);
  }, [appearanceMode]);

  const toggleAppearanceMode = () => {
    setAppearanceMode(prev => {
      // Cycle: supple-light → supple-dark → plain → supple-light
      switch (prev) {
        case 'supple-light':
          return 'supple-dark';
        case 'supple-dark':
          return 'plain';
        case 'plain':
          return 'supple-light';
        default:
          return 'supple-light';
      }
    });
  };

  const value = {
    appearanceMode,
    setAppearanceMode,
    toggleAppearanceMode,
    isSuppleMode: appearanceMode === 'supple-light' || appearanceMode === 'supple-dark',
    isSuppleLight: appearanceMode === 'supple-light',
    isSuppleDark: appearanceMode === 'supple-dark',
    isPlainMode: appearanceMode === 'plain'
  };

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
};